import { Formik, Form } from 'formik';
import {
   SwitchFormControl,
   TextFormControl,
   TextareaFormControl,
} from '~/ui-components/FormControls';
import { Button, Flex, Text } from '@chakra-ui/react';
import { CourseStageEditorFormControl } from './CourseStageEditor';
import { PageBar } from '~/pages/_shared';
import { CourseOptions } from './CourseOptions';
import { useLocalization } from '~/framework';

type CourseFormProps = {
   initialValues: CourseOptions;
   backHref: string;
   onSubmit: (formValues: CourseOptions) => any;
   dateValues?: {
      created_date: number;
      modified_date: number;
   };
};

export function CourseForm({ initialValues, dateValues, onSubmit, backHref }: CourseFormProps) {
   const { _js } = useLocalization();

   function validateTitle(title: string) {
      // title cannot be empty
      const trimmedTitle = title.trim();
      if (trimmedTitle === '') {
         return _js('The course title cannot be empty.');
      }

      // title cannot contain special characters
      const specialChars = /[#<>[\]|{},+?&/\\%:;$^*\t]/;
      if (['.', '..'].includes(trimmedTitle) || specialChars.test(title)) {
         return _js('The course title contains invalid characters.');
      }
      return null;
   }

   function validateDescription(description: string) {
      // description cannot be empty
      const trimmedDescription = description.trim();
      if (trimmedDescription === '') {
         return _js('The course description cannot be empty.');
      }
      return null;
   }

   return (
      <Formik initialValues={initialValues} onSubmit={onSubmit} validateOnBlur={true}>
         <Form>
            <PageBar backHref={backHref}>
               <Button colorScheme="blue" variant="solid" type="submit" mr={2}>
                  {_js('Save Changes')}
               </Button>
            </PageBar>
            <Flex mb={4}>
               <Flex direction="column" flex="1">
                  <SwitchFormControl
                     name="enabled"
                     label={_js('Course Visibility')}
                     onHelperText={_js('Enabled')}
                     offHelperText={_js('Disabled')}
                  />
                  <TextFormControl
                     name="title"
                     label={_js('Title')}
                     placeholder={_js('Course Title')}
                     validate={validateTitle}
                  />
                  <TextareaFormControl
                     name="contents"
                     label={_js('Description')}
                     placeholder={_js('Course Description')}
                     validate={validateDescription}
                     rows={4}
                  />
               </Flex>
               {dateValues && (
                  <Flex direction="column" justify="flex-end" ml={4} mb={5}>
                     <Flex direction="column" height="6.5rem" justify="space-between">
                        <Flex direction="column">
                           <Text mb={1} fontSize="xs" variant="subtitle">
                              {_js('Created Date')}
                           </Text>
                           <Text fontSize="sm">
                              {new Date(dateValues.created_date * 1000).toLocaleString()}
                           </Text>
                        </Flex>
                        <Flex direction="column">
                           <Text mb={1} fontSize="xs" variant="subtitle">
                              {_js('Modified Date')}
                           </Text>
                           <Text fontSize="sm" align="center">
                              {new Date(dateValues.modified_date * 1000).toLocaleString()}
                           </Text>
                        </Flex>
                     </Flex>
                  </Flex>
               )}
            </Flex>
            <CourseStageEditorFormControl />
         </Form>
      </Formik>
   );
}
